import { STORAGE_BASE } from '@/config/api'

/**
 * 当前页是 https 时，把图片地址升级为 https（需接口支持 https）
 */
function upgradeToHttpsIfPageIsSecure(absUrl) {
  if (typeof window === 'undefined' || !absUrl) return absUrl
  if (window.location.protocol !== 'https:') return absUrl
  try {
    const u = new URL(absUrl)
    if (u.protocol === 'http:') {
      const port = u.port ? `:${u.port}` : ''
      return `https://${u.hostname}${port}${u.pathname}${u.search}${u.hash}`
    }
  } catch (e) {
    /* ignore */
  }
  return absUrl
}

/**
 * 拼出与当前配置的 API 一致的绝对存储地址（入库、展示）
 */
export function buildAbsoluteStorageUrl(pathOrUrl) {
  if (pathOrUrl == null || pathOrUrl === '') return ''
  const s = String(pathOrUrl).trim()
  if (/^https?:\/\//i.test(s)) {
    return upgradeToHttpsIfPageIsSecure(s)
  }
  const rel = s.replace(/^\//, '')
  const base = String(STORAGE_BASE).replace(/\/?$/, '/')
  return upgradeToHttpsIfPageIsSecure(`${base}${rel}`)
}

/** 匹配 HTML 里出现的「任意域名 + /storage/ + 路径」 */
const ABS_STORAGE_URL_RE = /https?:\/\/[^"'\s<>]+\/storage\/[^"'\s<>]*/gi

/**
 * 富文本：任意错误域名（如 api.qy.com）的存储地址 → 仅 pathname（开发环境走代理）
 */
export function relativizeStorageUrlsInHtml(html) {
  if (process.env.NODE_ENV !== 'development' || !html || typeof html !== 'string') {
    return html
  }
  return html.replace(ABS_STORAGE_URL_RE, (full) => {
    try {
      const u = new URL(full.trim())
      return u.pathname + (u.search || '') + (u.hash || '')
    } catch (e) {
      return full
    }
  })
}

/**
 * 提交前：
 * 1) 任意域名的 /storage/... 统一为当前 VUE_APP_API_BASE 下的地址（修正后端笔误域名）
 * 2) 再把 src="/storage/ 这类相对路径换成绝对地址
 */
export function absolutizeStorageUrlsInHtml(html) {
  if (!html || typeof html !== 'string') return html
  const storageRoot = String(STORAGE_BASE).replace(/\/?$/, '')

  let out = html.replace(ABS_STORAGE_URL_RE, (full) => {
    try {
      const u = new URL(full.trim())
      if (!u.pathname.startsWith('/storage')) return full
      const rest = u.pathname.replace(/^\/storage\/?/, '/')
      return `${storageRoot}${rest === '/' ? '' : rest}${u.search}${u.hash}`
    } catch (e) {
      return full
    }
  })

  const base = String(STORAGE_BASE).replace(/\/$/, '')
  out = out
    .replace(/\bsrc="\/storage\//gi, `src="${base}/`)
    .replace(/\bsrc='\/storage\//gi, `src='${base}/`)
    .replace(/\bdata-href="\/storage\//gi, `data-href="${base}/`)
    .replace(/\bdata-href='\/storage\//gi, `data-href='${base}/`)
    .replace(/\bhref="\/storage\//gi, `href="${base}/`)
    .replace(/\bhref='\/storage\//gi, `href='${base}/`)

  return out
}

/**
 * 统一解析上传接口的 data：
 * - { path, url? }
 * - [{ path, url? }]
 * - { data: { path } } 嵌套一层
 * - data 为纯字符串路径
 */
export function normalizeUploadResponse(res) {
  let raw = res?.data
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.data != null && typeof raw.data === 'object') {
    raw = raw.data
  }
  if (raw == null) return null
  if (Array.isArray(raw)) {
    raw = raw[0]
  }
  if (typeof raw === 'string') {
    const p = raw.trim()
    return p ? { path: p, url: '' } : null
  }
  if (!raw || typeof raw !== 'object') return null
  const path = typeof raw.path === 'string' ? raw.path.trim() : ''
  const url = typeof raw.url === 'string' ? raw.url.trim() : ''
  return { path, url }
}

/** 存库用的相对路径（如 uploads/images/xxx.png） */
export function getUploadRelativePath(res) {
  const item = normalizeUploadResponse(res)
  return item?.path || ''
}

/**
 * 列表 / 封面等 el-image 预览：始终用「与 .env 接口域名一致」的绝对地址。
 * 开发环境若改用 /storage 走代理，代理未生效时 el-image 会加载失败并显示 FAILED。
 */
export function getStorageFileUrl(pathOrUrl) {
  return buildAbsoluteStorageUrl(pathOrUrl)
}

/**
 * 富文本上传 insertFn：优先 path，与封面预览一致用绝对地址（与你在浏览器直接打开图片的 URL 相同）。
 */
export function resolveUploadedImageUrl(res) {
  const item = normalizeUploadResponse(res)
  if (!item) return ''
  if (item.path) {
    return buildAbsoluteStorageUrl(item.path)
  }
  if (item.url) {
    return buildAbsoluteStorageUrl(item.url)
  }
  return ''
}

const DEFAULT_MAX_IMAGE_BYTES = 2 * 1024 * 1024

function loadImageFromBlob(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('图片加载失败'))
    }
    image.src = url
  })
}

function canvasToJpegBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b)
        else reject(new Error('图片编码失败'))
      },
      'image/jpeg',
      quality
    )
  })
}

/**
 * 若图片大于 maxBytes，则用 canvas 缩放 + JPEG 压缩，尽量压到不超过 maxBytes 再上传。
 * PNG 透明区域会铺白底；GIF 仅首帧。
 *
 * @param {File|Blob} file
 * @param {object} [options]
 * @param {number} [options.maxBytes=2097152] 目标最大体积（默认 2MB）
 * @param {number} [options.maxEdge=1920] 长边最大像素
 * @returns {Promise<File>}
 */
export async function compressImageIfNeeded(file, options = {}) {
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_IMAGE_BYTES
  const maxEdge = options.maxEdge ?? 1920

  if (!file || typeof file.size !== 'number') {
    throw new Error('无效的文件')
  }
  if (file.size <= maxBytes) {
    return file instanceof File
      ? file
      : new File([file], 'image.jpg', { type: file.type || 'image/jpeg', lastModified: Date.now() })
  }

  const img = await loadImageFromBlob(file)
  let w = img.naturalWidth || img.width
  let h = img.naturalHeight || img.height
  if (!w || !h) {
    throw new Error('无法读取图片尺寸')
  }

  let scale = Math.min(1, maxEdge / Math.max(w, h))
  let curW = Math.max(1, Math.round(w * scale))
  let curH = Math.max(1, Math.round(h * scale))

  const drawToBlob = async (width, height, quality) => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas 不可用')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
    ctx.drawImage(img, 0, 0, width, height)
    return canvasToJpegBlob(canvas, quality)
  }

  let quality = 0.9
  let blob = await drawToBlob(curW, curH, quality)

  const shrinkQuality = () => {
    quality = Math.max(0.32, quality - 0.06)
  }
  const shrinkSize = () => {
    curW = Math.max(128, Math.round(curW * 0.78))
    curH = Math.max(128, Math.round(curH * 0.78))
    quality = 0.85
  }

  let guard = 0
  while (blob.size > maxBytes && guard < 48) {
    guard += 1
    if (quality > 0.34) {
      shrinkQuality()
      blob = await drawToBlob(curW, curH, quality)
    } else if (curW > 128 || curH > 128) {
      shrinkSize()
      blob = await drawToBlob(curW, curH, quality)
    } else {
      shrinkQuality()
      blob = await drawToBlob(curW, curH, quality)
      if (blob.size > maxBytes) break
    }
  }

  const baseName =
    file instanceof File && file.name
      ? String(file.name).replace(/\.[^.]+$/, '')
      : 'image'
  return new File([blob], `${baseName || 'image'}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now()
  })
}
